/**
 * 「Powered by moovy studio」ウォーターマークのリンク先URLを組み立てるユーティリティ。
 *
 * ウォーターマークは動画個別ページ・プレイリストページ・埋め込みスクリプト・
 * 埋め込みモーダルの4系統に散らばっているため、リンク先の計測パラメータは
 * ここで一元的に生成する（付け忘れ・表記ゆれ防止）。
 *
 * ## 付与するパラメータ（GA4 の標準 UTM のみを使う）
 *
 * | パラメータ     | 値                          | GA4 のディメンション        |
 * | -------------- | --------------------------- | --------------------------- |
 * | `utm_source`   | 掲載ページのホスト名        | セッションの参照元          |
 * | `utm_medium`   | `referral` 固定             | セッションのメディア        |
 * | `utm_campaign` | `powered_by` 固定           | セッションのキャンペーン    |
 * | `utm_content`  | 設置面（{@link WatermarkPlacement}） | セッションの手動広告コンテンツ |
 * | `utm_term`     | テナントID（取得できる面のみ） | セッションの手動キーワード |
 *
 * - `utm_medium` を `referral` にしているのは、GA4 のデフォルトチャネルグループで
 *   「Referral」に分類させるため。独自の値にすると「Unassigned」に落ちて集計しづらい。
 * - `utm_campaign=powered_by` で「ウォーターマーク経由の流入」を一括で抽出できる。
 * - `utm_source` に掲載ドメインを入れることで「どのサイトの埋め込みから来たか」が、
 *   `utm_term` のテナントIDで「どの顧客の配信面から来たか」が分かる。
 * - 動画ID・シナリオIDは意図的に含めない（カーディナリティが跳ね上がり、
 *   GA4 のレポートで (other) に丸められるため）。
 */

import { SITE_URL } from './siteMetadata'

/** ウォーターマークのリンク先（パラメータ無しのベースURL） */
export const WATERMARK_BASE_URL = `${SITE_URL}/`

/** studio 本体のホスト名。SSR されるページの `utm_source` 既定値にも使う */
export const STUDIO_HOST = new URL(SITE_URL).hostname

/** ウォーターマークの設置面。`utm_content` にそのまま入る */
export type WatermarkPlacement =
  /** 動画個別ページ（PC: プレイヤー下 / SP: フッター） */
  | 'video_page'
  /** プレイリストページのフッター */
  | 'playlist'
  /** 埋め込みスクリプトが Light DOM に注入するクレジット（表示タイプ確定前） */
  | 'embed'
  /** 同上・SLIDER / TILE 型（スクリプト位置にインライン表示） */
  | 'embed_inline'
  /** 同上・POPUP 型（画面右下に固定表示） */
  | 'embed_popup'
  /** 埋め込みプレイヤーのPCモーダル内 */
  | 'embed_modal'

/** GA4 のデフォルトチャネルグループ「Referral」に載せるため固定 */
const WATERMARK_UTM_MEDIUM = 'referral'

/** ウォーターマーク経由の流入を一括抽出するための固定キャンペーン名 */
const WATERMARK_UTM_CAMPAIGN = 'powered_by'

/** ホスト名が取れなかった場合の `utm_source`（file:// や about:blank 等） */
const UNKNOWN_HOST = 'unknown'

export interface WatermarkLinkOptions {
  /** テナント（企業）ID。渡した場合のみ `utm_term` に入る */
  tenantId?: string | null
  /**
   * 掲載ページのホスト名（`utm_source`）。
   *
   * 省略時はブラウザ上なら `location.hostname`、それ以外（SSR）は {@link STUDIO_HOST}。
   * SSR されるページでは hydration 差分を避けるため必ず明示的に渡すこと。
   */
  host?: string | null
}

/** `utm_source` に入れるホスト名を解決する */
function resolveHost(explicit?: string | null): string {
  if (explicit) return explicit
  if (typeof window === 'undefined') return STUDIO_HOST
  return window.location?.hostname || UNKNOWN_HOST
}

/**
 * ウォーターマークのリンク先URLを組み立てる。
 *
 * @param placement 設置面（`utm_content` になる）
 * @param options テナントID・掲載ホスト名
 */
export function buildWatermarkUrl(
  placement: WatermarkPlacement,
  options: WatermarkLinkOptions = {},
): string {
  const url = new URL(WATERMARK_BASE_URL)
  url.searchParams.set('utm_source', resolveHost(options.host))
  url.searchParams.set('utm_medium', WATERMARK_UTM_MEDIUM)
  url.searchParams.set('utm_campaign', WATERMARK_UTM_CAMPAIGN)
  url.searchParams.set('utm_content', placement)

  const tenantId = options.tenantId?.trim()
  if (tenantId) url.searchParams.set('utm_term', tenantId)

  return url.toString()
}
